import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, Link, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { ReactNode, useContext } from "react";
import { useMutation } from "react-relay";
import { InternBuildPipelinesPanelSubmitMutation } from "./__generated__/InternBuildPipelinesPanelSubmitMutation.graphql";
import SnackbarContext from "../../../contexts/SnackbarContext";
import InternCard from "../components/InternCard";
import InternStatusBadge from "../components/InternStatusBadge";
import InternTable, { InternTableColumn } from "../components/InternTable";
import { PipelineRow, getPipelineSubmitState } from "./submitState";

export type { PipelineRow };

interface Props {
  brandId: string;
  pipelines: PipelineRow[];
  onSubmitted?: () => void;
  titleAction?: ReactNode;
}

const submitMutation = graphql`
  mutation InternBuildPipelinesPanelSubmitMutation(
    $brandId: ID!
    $pipelineId: ID!
    $platform: String!
  ) {
    submitForReview(
      brandId: $brandId
      pipelineId: $pipelineId
      platform: $platform
    ) {
      success
      message
    }
  }
`;

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getPipelineStatusBadge = (status: string) => {
  switch (status) {
    case "success":
      return (
        <InternStatusBadge status="success" label="Success" size="small" />
      );
    case "failed":
      return <InternStatusBadge status="error" label="Failed" size="small" />;
    case "pending":
    case "running":
      return (
        <InternStatusBadge status="warning" label="In Progress" size="small" />
      );
    case "canceled":
      return (
        <InternStatusBadge status="default" label="Canceled" size="small" />
      );
    default:
      return <InternStatusBadge status="default" label={status} size="small" />;
  }
};

const getSubmitOutcomeLabel = (row: PipelineRow): string => {
  if (getPipelineSubmitState(row) === "stuck") return "Needs manual submit";

  switch (row.submitOutcome) {
    case "ready_to_submit":
      return "Ready to submit";
    case "submitted":
      return "Submitted";
    case "skipped_never_published":
      return "Needs manual submit";
    case "skipped_first_build":
      return "First build";
    case "failed":
      return "Submit failed";
    case "pending":
      return "Processing";
    case "not_applicable":
      return "—";
    default:
      return row.submitOutcome || "—";
  }
};

const getSubmitOutcomeColor = (row: PipelineRow): string => {
  if (getPipelineSubmitState(row) === "stuck") return "#b45309";

  switch (row.submitOutcome) {
    case "ready_to_submit":
      return "#b45309";
    case "submitted":
      return "var(--intern-status-success)";
    case "failed":
      return "#dc2626";
    default:
      return "var(--intern-text-secondary)";
  }
};

const formatFieldName = (field: string): string =>
  field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getTriggerFields = (triggerReason: unknown): string[] => {
  if (!Array.isArray(triggerReason)) return [];
  const seen = new Set<string>();
  const fields: string[] = [];
  for (const item of triggerReason) {
    const field =
      item && typeof item === "object" && "field" in item
        ? String((item as { field: unknown }).field)
        : "";
    if (!field || seen.has(field)) continue;
    seen.add(field);
    fields.push(field);
  }
  return fields;
};

const circleCiSearchUrl = (platform: string) => {
  const project = platform === "ios" ? "kinn-ios" : "kinn4a";
  return `https://app.circleci.com/pipelines/github/reactivapp-kin/${project}?filter=all`;
};

const InternBuildPipelinesPanel = ({
  brandId,
  pipelines,
  onSubmitted,
  titleAction,
}: Props) => {
  const snackbarContext = useContext(SnackbarContext);
  const [submitForReview, isSubmitting] =
    useMutation<InternBuildPipelinesPanelSubmitMutation>(submitMutation);

  const handleSubmit = (pipelineId: string, platform: string) => {
    submitForReview({
      variables: { brandId, pipelineId, platform },
      onCompleted: (data) => {
        if (!data.submitForReview?.success) {
          snackbarContext?.openSnackbar(
            data.submitForReview?.message || "Submit failed",
            "error"
          );
        } else {
          snackbarContext?.openSnackbar(
            data.submitForReview.message || "Submitted for review",
            "success"
          );
          onSubmitted?.();
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("Submit failed", "error");
      },
    });
  };

  const columns: InternTableColumn<PipelineRow>[] = [
    {
      id: "platform",
      label: "Platform",
      width: 88,
      render: (row) => (
        <Chip
          label={row.platform.toUpperCase()}
          size="small"
          sx={{
            height: 22,
            bgcolor:
              row.platform === "ios"
                ? "rgba(0, 122, 255, 0.1)"
                : "rgba(61, 220, 132, 0.1)",
            color: row.platform === "ios" ? "#007AFF" : "#15803d",
            fontWeight: 700,
            fontSize: "0.625rem",
            letterSpacing: "0.04em",
          }}
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      width: 110,
      render: (row) => getPipelineStatusBadge(row.status),
    },
    {
      id: "trigger",
      label: "Source",
      width: 180,
      render: (row) => {
        const fields = getTriggerFields(row.triggerReason).filter(
          (f) => f !== "manual_kickoff"
        );
        const shown = fields.slice(0, 2);
        const extra = fields.length - shown.length;

        return (
          <Stack spacing={0.35}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
                fontWeight: 600,
              }}
            >
              {row.isAutomatic ? "Auto" : "Manual"}
            </Typography>
            {shown.length > 0 ? (
              <Stack direction="row" spacing={0.35} flexWrap="wrap" useFlexGap>
                {shown.map((field) => (
                  <Chip
                    key={field}
                    label={formatFieldName(field)}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.5625rem",
                      fontWeight: 600,
                      bgcolor: "var(--intern-bg-secondary)",
                      color: "var(--intern-text-tertiary)",
                    }}
                  />
                ))}
                {extra > 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--intern-text-tertiary)",
                      fontSize: "0.5625rem",
                      fontWeight: 600,
                      alignSelf: "center",
                    }}
                  >
                    +{extra}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        );
      },
    },
    {
      id: "version",
      label: "Version",
      width: 120,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-primary)",
            fontSize: "0.75rem",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {row.appVersion ? `v${row.appVersion}` : "—"}
          {row.appVersion && row.appBuildNumber ? (
            <Box
              component="span"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontWeight: 500,
                ml: 0.5,
              }}
            >
              ({row.appBuildNumber})
            </Box>
          ) : null}
        </Typography>
      ),
    },
    {
      id: "submit",
      label: "Review",
      width: 150,
      render: (row) => (
        <Stack spacing={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: getSubmitOutcomeColor(row),
              fontSize: "0.6875rem",
              fontWeight: 600,
            }}
          >
            {getSubmitOutcomeLabel(row)}
          </Typography>
          {row.submitOutcomeDetail ? (
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.submitOutcomeDetail}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: "startedAt",
      label: "Started",
      width: 120,
      render: (row) => (
        <Typography
          variant="caption"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.6875rem",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDate(row.startedAt)}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "",
      width: 140,
      render: (row) => {
        const submitState = getPipelineSubmitState(row);

        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Link
              href={circleCiSearchUrl(row.platform)}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              title={row.circleciPipelineId}
              sx={{
                display: "inline-flex",
                p: 0.5,
                borderRadius: 1,
                color: "var(--intern-text-tertiary)",
                "&:hover": {
                  color: "var(--intern-text-primary)",
                  bgcolor: "var(--intern-bg-secondary)",
                },
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </Link>
            {submitState ? (
              <LoadingButton
                size="small"
                variant="outlined"
                loading={isSubmitting}
                startIcon={
                  <RocketLaunchIcon sx={{ fontSize: "0.875rem !important" }} />
                }
                onClick={() => handleSubmit(row.id, row.platform)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.25,
                  minHeight: 28,
                  borderColor: "var(--intern-border-default)",
                  color: "var(--intern-text-secondary)",
                  "&:hover": {
                    borderColor: "var(--intern-text-tertiary)",
                    bgcolor: "var(--intern-bg-secondary)",
                  },
                }}
              >
                {submitState === "retry" ? "Retry" : "Submit"}
              </LoadingButton>
            ) : null}
          </Stack>
        );
      },
    },
  ];

  return (
    <InternCard
      title="Pipelines"
      subtitle="CircleCI runs — automatic and manual"
      variant="compact"
      noPadding={pipelines.length > 0}
      titleAction={titleAction}
    >
      {pipelines.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 5,
            color: "var(--intern-text-tertiary)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
          >
            No pipelines yet
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.6875rem", mt: 0.5, opacity: 0.8 }}
          >
            Push a build to start tracking here
          </Typography>
        </Box>
      ) : (
        <InternTable
          columns={columns}
          data={pipelines}
          keyExtractor={(row) => row.id}
          variant="compact"
          emptyMessage="No pipelines"
        />
      )}
    </InternCard>
  );
};

export default InternBuildPipelinesPanel;
